import React, { useState, useEffect } from "react";
import * as Yup from "yup";
import styled from "@emotion/styled";
import { useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { showSuccessSnackbar, showErrorSnackbar } from "../../redux/slices/snackbar";

import {
  Alert as MuiAlert,
  Box,
  Breadcrumbs as MuiBreadcrumbs,
  Card as MuiCard,
  CardContent,
  CircularProgress,
  Grid,
  CardHeader,
  Typography,
} from "@mui/material";
import { spacing } from "@mui/system";
import { yupResolver } from "@hookform/resolvers/yup";
import { useForm, FormProvider } from "react-hook-form";

import SaveCancel from "../../components/SaveCancel";
import useAgreementTracker from "../../hooks/useAgreementTracker";
import AgreementTrackerFields from "./components/AgreementTrackerFields";

const Breadcrumbs = styled(MuiBreadcrumbs)(spacing);
const Card = styled(MuiCard)(spacing);
const Alert = styled(MuiAlert)(spacing);

const validationSchema = Yup.object().shape({
  tenant: Yup.object()
    .shape({
      label: Yup.string().required(),
      value: Yup.string().required(),
    })
    .nullable()
    .required("Tenant is required"),
  dateOfAgreement: Yup.string().required("Required"),
});

function EditAgreementTracker() {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { state } = useLocation();

  const { getAgreementTracker, agreementTracker, updateAgreementTracker, isSubmitting } =
    useAgreementTracker();

  const methods = useForm({
    resolver: yupResolver(validationSchema),
  });

  const { reset, handleSubmit } = methods;

  useEffect(() => {
    if (state?.id) {
      getAgreementTracker(state.id);
    }
  }, [state?.id]);

  useEffect(() => {
    if (
      agreementTracker &&
      Object.keys(agreementTracker).length !== 0 &&
      agreementTracker.agreementTrackerId === state?.id
    ) {
      const dateStr = agreementTracker.dateOfAgreementStr
        ? agreementTracker.dateOfAgreementStr.split(" ")[0].split("-").reverse().join("-")
        : "";
      const dateObj = dateStr ? new Date(dateStr) : null;

      reset({
        agreementTrackerId: agreementTracker.agreementTrackerId,
        agreementNumber: agreementTracker.agreementNumber,
        status: agreementTracker.status,
        dateOfAgreement: dateObj,
        dateOfAgreementStr: dateStr,
        tenant: agreementTracker.tenant
          ? {
              label: agreementTracker.tenant.tenantName,
              value: agreementTracker.tenant.tenantId,
            }
          : null,
      });
    }
  }, [agreementTracker]);

  const onSubmit = async (data) => {
    try {
      const payload = {
        agreementTrackerId: data.agreementTrackerId,
        agreementNumber: data.agreementNumber,
        status: data.status,
        dateOfAgreementStr: data.dateOfAgreementStr,
        tenant: {
          tenantId: data.tenant?.value,
          tenantName: data.tenant?.label,
        },
      };

      await updateAgreementTracker(payload);
      dispatch(showSuccessSnackbar("Agreement Tracker updated successfully!"));
      navigate("/agreement-tracker/list");
    } catch (err) {
      dispatch(showErrorSnackbar("Failed to update Agreement Tracker"));
    }
  };

  const cancelBtn = () => {
    navigate("/agreement-tracker/list");
  };

  const breadcrumb = () => {
    return (
      <Breadcrumbs aria-label="Breadcrumb" mt={2}>
        <Typography>Agreement Tracker</Typography>
        <Typography>Edit</Typography>
      </Breadcrumbs>
    );
  };

  return (
    <React.Fragment>
      <FormProvider {...methods}>
        <Card mb={6}>
          <CardHeader title={t("edit_agreement_tracker")} subheader={breadcrumb()} />
          <CardContent>
            {isSubmitting ? (
              <Box display="flex" justifyContent="center" my={6}>
                <CircularProgress />
              </Box>
            ) : (
              <form onSubmit={handleSubmit(onSubmit)}>
                <AgreementTrackerFields fields={validationSchema.fields} />
                <SaveCancel cancelBtn={cancelBtn} resetForm={reset} />
              </form>
            )}
          </CardContent>
        </Card>
      </FormProvider>
    </React.Fragment>
  );
}

export default EditAgreementTracker;
